/* Product Categories is the sole source of hierarchy; JS only manages display. */
if (!customElements.get('cozy-category-menu')) {
  customElements.define('cozy-category-menu', class extends HTMLElement {
    connectedCallback() {
      // Streaming HTML and section replacement can connect the host before its children.
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.initialize(), { once: true });
      } else {
        queueMicrotask(() => this.initialize());
      }
    }

    initialize() {
      if (this.controller || !this.isConnected) return;
      this.controller = new AbortController();
      const on = (target, type, handler) => target.addEventListener(type, handler, { signal: this.controller.signal });
      this.dialog = this.querySelector('dialog');
      this.triggers = [...document.querySelectorAll(`[data-category-open][aria-controls="${this.dialog.id}"]`)];
      this.tabs = [...this.querySelectorAll('[data-category-tab]')];
      this.groups = [...this.querySelectorAll('[data-category-group]')];
      this.groupNodes = new Map(this.groups.map((group, index) => [group, {
        toggle: group.querySelector('.cn-category-group__toggle'),
        panel: group.querySelector('.cn-category-group__panel'),
        children: [...group.querySelectorAll('[data-category-child]')],
        tab: this.tabs[index]
      }]));
      this.search = this.querySelector('[data-category-search]');
      this.empty = this.querySelector('[data-category-empty]');
      this.mobile = window.matchMedia('(max-width: 1023px)');
      this.activeId = this.tabs[0]?.dataset.categoryTab;
      this.openIds = new Set([this.activeId]);
      // MenuDrawer moves in the DOM; delegated triggers keep both entry points connected.
      on(document, 'click', event => {
        const trigger = event.target.closest('[data-category-open]');
        if (trigger?.getAttribute('aria-controls') !== this.dialog.id) return;
        event.preventDefault();
        this.open(trigger);
      });
      if (Shopify.designMode) {
        on(document, 'shopify:block:select', event => {
          if (this.menuDrawer?.contains(event.target)) this.restoreMenu();
        });
      }
      on(this.dialog.querySelector('[data-category-back]'), 'click', () => this.backToMenu());
      on(this.dialog.querySelector('[data-category-close]'), 'click', () => this.close());
      on(this.dialog, 'cancel', event => { event.preventDefault(); this.close(); });
      on(this.dialog, 'keydown', event => {
        if (event.key === 'Escape' && this.presentation !== 'menu') {
          event.preventDefault();
          event.stopPropagation();
          this.close();
        }
      });
      on(this.dialog, 'close', () => {
        if (this.presentation !== 'menu' && !this.dialog.open) this.afterClose();
      });
      on(this.dialog, 'click', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.close();
      });
      this.tabs.forEach(tab => {
        on(tab, 'click', () => this.select(tab.dataset.categoryTab));
        on(tab, 'keydown', event => this.onTabKey(event, tab));
      });
      this.groups.forEach(group => {
        on(this.groupNodes.get(group).toggle, 'click', () => {
          const id = group.dataset.categoryGroup;
          this.openIds.has(id) ? this.openIds.delete(id) : this.openIds.add(id);
          this.render();
        });
      });
      on(this.search, 'input', () => this.filter());
      on(this.search, 'search', () => this.filter());
      on(this.mobile, 'change', () => {
        this.position(); this.render();
        if (this.dialog.open && this.presentation !== 'menu') this.dialog.querySelector('[data-category-close]').focus({ preventScroll: true });
      });
      on(window, 'resize', () => { if (this.dialog.open) this.position(); });
      this.render();
    }

    disconnectedCallback() {
      if (this.menuDrawer) this.restoreMenu();
      else if (this.dialog?.open) { this.dialog.close(); this.afterClose(); }
      this.controller?.abort();
      this.controller = null;
    }

    open(trigger) {
      if (this.dialog.open) return;
      this.presentation = trigger.dataset.categoryPresentation || 'header';
      this.dialog.dataset.presentation = this.presentation;
      this.returnFocus = trigger;
      this.search.value = '';
      this.activeId = this.tabs[0]?.dataset.categoryTab;
      this.openIds = new Set(this.presentation === 'menu' ? [] : [this.activeId]);
      this.filter();

      if (this.presentation === 'menu') {
        this.menuTrigger = trigger;
        this.menuDrawer = trigger.closest('#MenuDrawer');
        this.menuView = this.menuDrawer.querySelector('[data-main-menu-view]');
        this.menuSlot = this.menuDrawer.querySelector('[data-menu-categories]');
        this.menuView.hidden = true;
        this.menuSlot.hidden = false;
        // Move the one category DOM into the already-open drawer, without another modal.
        this.menuSlot.append(this.dialog);
        this.dialog.setAttribute('role', 'region');
        this.dialog.show();
        this.menuController = new AbortController();
        this.menuDrawer.addEventListener('drawer:handleAfterHide', () => this.restoreMenu(), { signal: this.menuController.signal });
        // The theme trap includes hidden links; use the visible view's current boundaries.
        this.menuDrawer.addEventListener('keydown', event => this.trapMenuTab(event), { capture: true, signal: this.menuController.signal });
        FoxTheme.a11y.trapFocus(this.menuDrawer, this.dialog.querySelector('[data-category-back]'));
      } else {
        this.dialog.removeAttribute('role');
        this.position();
        this.previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        this.dialog.showModal();
        this.position();
        this.dialog.querySelector('[data-category-close]').focus({ preventScroll: true });
      }
      this.triggers.forEach(button => button.setAttribute('aria-expanded', 'true'));
      this.dialog.querySelector('.cn-category-dialog__parents').scrollTop = 0;
      this.dialog.querySelector('.cn-category-dialog__groups').scrollTop = 0;
      this.dialog.querySelector('.cn-category-dialog__body').scrollTop = 0;
    }

    trapMenuTab(event) {
      if (event.key !== 'Tab') return;
      const visible = FoxTheme.a11y.getFocusableElements(this.menuDrawer).filter(node => node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden');
      const first = visible[0];
      const last = visible[visible.length - 1];
      if ((event.shiftKey && document.activeElement === first) || (!event.shiftKey && document.activeElement === last)) {
        event.preventDefault();
        event.stopPropagation();
        (event.shiftKey ? last : first)?.focus();
      }
    }

    close() {
      if (this.menuDrawer) this.menuDrawer.hide();
      else this.dialog.close();
    }

    backToMenu() { this.restoreMenu(); }

    restoreMenu() {
      if (!this.menuDrawer) return;
      const drawer = this.menuDrawer;
      this.menuController?.abort();
      this.dialog.close();
      this.append(this.dialog);
      this.menuSlot.hidden = true;
      this.menuView.hidden = false;
      this.triggers.forEach(button => button.setAttribute('aria-expanded', 'false'));
      // The native trap caches focusable nodes; refresh it after swapping views.
      if (drawer.open) FoxTheme.a11y.trapFocus(drawer, this.menuTrigger);
      this.menuDrawer = null;
    }

    afterClose() {
      document.documentElement.style.overflow = this.previousOverflow || '';
      this.triggers.forEach(button => button.setAttribute('aria-expanded', 'false'));
      this.returnFocus?.focus({ preventScroll: true });
    }

    position() {
      const anchor = this.returnFocus?.closest('.cn-categories__rail');
      if (this.presentation === 'menu' || !anchor) return;
      const rail = anchor.getBoundingClientRect();
      const top = Math.min(Math.max(8, rail.bottom), Math.max(8, window.innerHeight - 360));
      this.dialog.style.setProperty('--category-top', `${Math.round(top)}px`);
      this.dialog.style.setProperty('--category-left', `${Math.round(rail.left)}px`);
      this.dialog.style.setProperty('--category-width', `${Math.round(rail.width)}px`);
    }

    select(id) {
      this.activeId = id;
      this.openIds.add(id);
      this.render();
      this.dialog.querySelector('.cn-category-dialog__groups').scrollTop = 0;
    }

    onTabKey(event, tab) {
      const visible = this.tabs.filter(item => !item.hidden);
      let index = visible.indexOf(tab);
      if (event.key === 'ArrowDown') index = (index + 1) % visible.length;
      else if (event.key === 'ArrowUp') index = (index - 1 + visible.length) % visible.length;
      else if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = visible.length - 1;
      else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.groups.find(group => group.dataset.categoryGroup === this.activeId)?.querySelector('li:not([hidden]) a')?.focus();
        return;
      } else return;
      event.preventDefault();
      this.select(visible[index].dataset.categoryTab);
      visible[index].focus();
    }

    normalize(value) { return value.normalize('NFKC').toLocaleLowerCase().trim(); }

    filter() {
      const term = this.normalize(this.search.value);
      this.groups.forEach((group, index) => {
        const parentMatch = this.normalize(group.dataset.categoryName).includes(term);
        const { children } = this.groupNodes.get(group);
        children.forEach(child => { child.hidden = !parentMatch && !this.normalize(child.dataset.categoryName).includes(term); });
        const matched = parentMatch || children.some(child => !child.hidden);
        group.hidden = !matched;
        this.tabs[index].hidden = !matched;
        if (term && matched) this.openIds.add(group.dataset.categoryGroup);
      });
      const visible = this.tabs.filter(tab => !tab.hidden);
      this.empty.hidden = visible.length > 0;
      if (!visible.some(tab => tab.dataset.categoryTab === this.activeId)) this.activeId = visible[0]?.dataset.categoryTab;
      this.render();
    }

    render() {
      const accordion = this.mobile.matches || this.presentation === 'menu';
      this.dialog.dataset.layout = accordion ? 'accordion' : 'tabs';
      this.tabs.forEach(tab => {
        const selected = tab.dataset.categoryTab === this.activeId;
        tab.setAttribute('aria-selected', String(selected));
        tab.tabIndex = selected ? 0 : -1;
      });
      this.groups.forEach(group => {
        const id = group.dataset.categoryGroup;
        const { toggle, panel, tab } = this.groupNodes.get(group);
        const expanded = accordion ? this.openIds.has(id) : id === this.activeId;
        panel.hidden = !expanded;
        toggle.setAttribute('aria-expanded', String(expanded));
        panel.setAttribute('role', accordion ? 'region' : 'tabpanel');
        panel.setAttribute('aria-labelledby', accordion ? toggle.id : tab.id);
      });
    }
  });
}
